import { createSelector } from 'reselect'
import _ from 'lodash'
import { isNotEmptyElement, STABLE_EMPTY_ARRAY } from '@feedyou/utils'
import { GlobalStorage, MutationDefinition } from '@feedyou-ai/feedbot-schema'

import { mergeStoragesByName } from './utils'
import {
  allStepsSelector,
  botMutationsSelector,
  nlpSelector
} from '../../store/flow/selectors/selectors'
import { Storage } from './types'
import { Dialogs } from '../../components/steps/types'
import { extractUserStorages } from './utils/extractUserStorages'
import { extractNlpStorages } from './utils/extractNlpStorages'
import { extractIntegrationStorages } from './utils/extractIntegrationStorages'
import { getSystemStorages } from './utils/getSystemStorages'
import { extractWebchatUserDataStorages } from './utils/extractWebchatUserDataStorages'
import { botVariablesSelector } from '../../store/flow/selectors/botVariablesSelector'
import { dialogsSelector } from '../../store/flow/selectors/dialogsSelector'
import {
  storageSearchQuerySelector,
  visibleStorageTypesSelector
} from '../../store/dock/sections/storages/selectors'
import { botFlowSelector } from '../../store/flow/selectors/botFlowSelector'
import { RootState } from '../../reducers'

export const nlpStoragesSelector = createSelector(
  nlpSelector, // I <3 PRETTIER
  (nlp): Storage[] => {
    if (!nlp?.models) {
      return []
    }

    return nlp.models.map(extractNlpStorages).flat().filter(isNotEmptyElement)
  }
)

export const webChatUserDataStoragesSelector = createSelector(botFlowSelector, (flow): Storage[] =>
  flow ? extractWebchatUserDataStorages(flow.channels) : STABLE_EMPTY_ARRAY
)

export const systemStoragesSelector = createSelector(getSystemStorages)

export const userStoragesSelector = createSelector(
  dialogsSelector, // I <3 PRETTIER
  botMutationsSelector,
  (dialogs: Dialogs, botMutations: MutationDefinition[] = []): Storage[] => [
    ...extractUserStorages(Object.values(dialogs)),
    ...botMutations.map(botMutation => ({ name: botMutation.id, storageType: ['user'] } as Storage))
  ]
)

export const usedIntegrationStoragesSelector = createSelector(allStepsSelector, allSteps => {
  return extractIntegrationStorages(allSteps)
})

export const usedBotVariablesSelector = createSelector(
  botVariablesSelector,
  (botVariables): Storage[] => {
    return Object.keys(botVariables).map(name => ({
      name,
      storageType: ['environment']
    }))
  }
)

const globalStoragesSelector = createSelector(
  (state: RootState) => botFlowSelector(state)?.globalStorages,
  (globalStorages): Storage[] => {
    return Object.values(globalStorages ?? {})
      .filter(isNotEmptyElement)
      .map((globVar: GlobalStorage) => ({
        name: globVar.name,
        storageType: ['global'],
        value: globVar.value
      }))
  }
)

export const allStoragesSelector = createSelector(
  [
    nlpStoragesSelector,
    webChatUserDataStoragesSelector,
    systemStoragesSelector,
    userStoragesSelector,
    usedIntegrationStoragesSelector,
    usedBotVariablesSelector,
    globalStoragesSelector
  ],
  (
    nlpStorages,
    webChatUserDataStorages,
    systemStorages,
    userStorages,
    integrationStorages,
    botVariables,
    globalStorages
  ): Storage[] => {
    const storages = mergeStoragesByName([
      ...userStorages,
      ...nlpStorages,
      ...webChatUserDataStorages,
      ...systemStorages,
      ...integrationStorages,
      ...botVariables,
      ...globalStorages
    ]).filter(storage => !!storage.name)

    return _.sortBy(storages, 'name')
  }
)

export const allStoragesFilteredByTypesAndSearchSelector = createSelector(
  allStoragesSelector,
  visibleStorageTypesSelector,
  storageSearchQuerySelector,
  (allStorages, visibleTypes, searchQuery) => {
    return allStorages
      ?.filter(storage =>
        storage?.storageType?.some(storageType => visibleTypes?.includes(storageType))
      )
      .filter(storage => storage?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
  }
)
